import { RubyGemMark } from '../../components/RubyGemMark';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle, Building2, Info, Mail, Plus, RefreshCw, Save, X,
} from 'lucide-react';
import { FieldLabel } from '@documents-ui';
import type {
  CompanyAddress, CompanyEntityType, CompanyProfile, CompanySignatory,
} from '@platform/shared-types';
import {
  COMPANY_ENTITY_TYPES, CompanyProfileFieldsSchema, CompanyProfileInputSchema,
} from '@platform/shared-types';
import { StaffPage } from '../../components/staff/StaffChrome';
import LegalNav from './LegalNav';
import SearchableDropdown from '../../components/SearchableDropdown';
import { documentsExtApi } from '../../services/documentsExtApi';
import { toastService } from '../../services/toastService';
import { Button, INPUT_CLASS } from './rubyChrome';
import { RubyLoader } from '../../components/RubyLoader';

/**
 * LEGAL / COMPANY PROFILE — the platform's own legal identity, ONE row.
 *
 * Every generated document (agreements, policies, the merge fields any of
 * them read) projects `company_*` fields from this row rather than a name
 * hard-coded per template. A 404 from the API is not a failure — it means
 * the row has never been saved, and the screen just renders an empty form
 * so the first save creates it.
 *
 * Validated client-side with the SAME zod schema the service enforces
 * (`CompanyProfileInputSchema`), so Save is
 * disabled for exactly the reasons the server would reject the write.
 */

/**
 * Every runtime (non-`type`) value this page imports from
 * `@platform/shared-types` is a RUNTIME value, and a Vite dep pre-bundle
 * built before that export existed hands back `undefined` for it — at
 * module scope, which would white-screen the whole page instead of failing
 * where it is used. This repo already learned that lesson once
 * (`documentCategories.ts`'s `SEED`): degrading costs a screen some of its
 * polish, crashing costs the page. One helper keeps the remedy wording
 * identical across every guard below, so it cannot drift as more get added
 * — the fix is always the same: RESTART the dev server. Deleting
 * `node_modules/.vite` under a running server does nothing, because Vite
 * keeps the optimised bundle in memory and rewrites the directory
 * underneath it.
 */
function warnStaleSharedTypesExport(what: string, degradedTo: string): void {
  console.error(
    `@platform/shared-types exported no usable ${what} on CompanyProfilePage — a stale `
    + 'Vite pre-bundle. RESTART the dev server: deleting node_modules/.vite under a '
    + 'running server does nothing, because Vite keeps the optimised bundle in memory '
    + `and rewrites the directory. ${degradedTo}`,
  );
}

/**
 * The client-side validation schema, guarded — see `warnStaleSharedTypesExport`
 * above. Degrades to `null`: with no schema to validate against, `canSave`
 * below is hard-`false`, so a missing schema can only ever DISABLE the
 * form, never let an unvalidated payload through.
 */
const PROFILE_SCHEMA = (() => {
  if (CompanyProfileInputSchema && typeof CompanyProfileInputSchema.safeParse === 'function') {
    return CompanyProfileInputSchema;
  }
  warnStaleSharedTypesExport(
    'CompanyProfileInputSchema',
    'The form will render but cannot validate, so Save stays disabled until the schema is back.',
  );
  return null;
})();

const ENTITY_TYPE_LABEL: Record<CompanyEntityType, string> = {
  single_member_llc: 'Single-Member LLC',
  multi_member_llc: 'Multi-Member LLC',
  corporation: 'Corporation',
  s_corporation: 'S Corporation',
  sole_proprietorship: 'Sole Proprietorship',
};

/**
 * The entity-type dropdown's options, guarded — see `warnStaleSharedTypesExport`
 * above. Unlike `PROFILE_SCHEMA`, this degrades to FULL working behaviour
 * rather than a disabled state: `ENTITY_TYPE_LABEL` is a local literal,
 * always available, and its five keys are exactly the five entity types, so
 * deriving options from it keeps the picker complete even when the import
 * is missing. Only client-side validation is lost either way, and
 * `PROFILE_SCHEMA`'s own guard already disables Save for that.
 */
const ENTITY_TYPE_OPTIONS = (() => {
  if (Array.isArray(COMPANY_ENTITY_TYPES)) {
    return COMPANY_ENTITY_TYPES.map((v) => ({ value: v, label: ENTITY_TYPE_LABEL[v] }));
  }
  warnStaleSharedTypesExport(
    'COMPANY_ENTITY_TYPES (not an array)',
    'Falling back to the compiled-in label list so the entity-type picker keeps all five options.',
  );
  return (Object.keys(ENTITY_TYPE_LABEL) as CompanyEntityType[])
    .map((v) => ({ value: v, label: ENTITY_TYPE_LABEL[v] }));
})();

/** USPS two-letter codes — every picker in this screen that asks for "state" uses this same list. */
const US_STATES: [string, string][] = [
  ['AL', 'Alabama'], ['AK', 'Alaska'], ['AZ', 'Arizona'], ['AR', 'Arkansas'], ['CA', 'California'],
  ['CO', 'Colorado'], ['CT', 'Connecticut'], ['DE', 'Delaware'], ['DC', 'District of Columbia'],
  ['FL', 'Florida'], ['GA', 'Georgia'], ['HI', 'Hawaii'], ['ID', 'Idaho'], ['IL', 'Illinois'],
  ['IN', 'Indiana'], ['IA', 'Iowa'], ['KS', 'Kansas'], ['KY', 'Kentucky'], ['LA', 'Louisiana'],
  ['ME', 'Maine'], ['MD', 'Maryland'], ['MA', 'Massachusetts'], ['MI', 'Michigan'], ['MN', 'Minnesota'],
  ['MS', 'Mississippi'], ['MO', 'Missouri'], ['MT', 'Montana'], ['NE', 'Nebraska'], ['NV', 'Nevada'],
  ['NH', 'New Hampshire'], ['NJ', 'New Jersey'], ['NM', 'New Mexico'], ['NY', 'New York'],
  ['NC', 'North Carolina'], ['ND', 'North Dakota'], ['OH', 'Ohio'], ['OK', 'Oklahoma'], ['OR', 'Oregon'],
  ['PA', 'Pennsylvania'], ['PR', 'Puerto Rico'], ['RI', 'Rhode Island'], ['SC', 'South Carolina'],
  ['SD', 'South Dakota'], ['TN', 'Tennessee'], ['TX', 'Texas'], ['UT', 'Utah'], ['VT', 'Vermont'],
  ['VA', 'Virginia'], ['WA', 'Washington'], ['WV', 'West Virginia'], ['WI', 'Wisconsin'], ['WY', 'Wyoming'],
];
const STATE_OPTIONS = US_STATES.map(([value, label]) => ({ value, label: `${value} — ${label}` }));

interface AddressForm {
  line1: string;
  line2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

const EMPTY_ADDRESS: AddressForm = { line1: '', line2: '', city: '', state: '', postalCode: '', country: 'US' };

interface FormState {
  legalEntityName: string;
  dba: string;
  entityType: CompanyEntityType | '';
  stateOfFormation: string;
  ein: string;
  registeredAddress: AddressForm;
  sameAsRegistered: boolean;
  mailingAddress: AddressForm;
  phone: string;
  website: string;
  supportEmail: string;
  billingEmail: string;
  accountsEmail: string;
  salesEmail: string;
  adminEmail: string;
  notificationEmails: string[];
  governingLawState: string;
  venueCounty: string;
  /**
   * WHO MAY SIGN FOR US. Held on the form exactly as the schema holds it — a
   * list plus the id of the default — because an `isDefault` flag per row
   * permits two defaults and there is no truthful answer to which signs.
   */
  signatories: CompanySignatory[];
  defaultSignatoryId: string;
}

const EMPTY_FORM: FormState = {
  legalEntityName: '',
  dba: '',
  entityType: '',
  stateOfFormation: '',
  ein: '',
  registeredAddress: { ...EMPTY_ADDRESS },
  sameAsRegistered: true,
  mailingAddress: { ...EMPTY_ADDRESS },
  phone: '',
  website: '',
  supportEmail: '',
  billingEmail: '',
  accountsEmail: '',
  salesEmail: '',
  adminEmail: '',
  notificationEmails: [],
  governingLawState: '',
  venueCounty: '',
  signatories: [],
  defaultSignatoryId: '',
};

function addressToForm(a?: CompanyAddress): AddressForm {
  if (!a) return { ...EMPTY_ADDRESS };
  return { line1: a.line1, line2: a.line2 ?? '', city: a.city, state: a.state, postalCode: a.postalCode, country: a.country };
}

function profileToForm(p: CompanyProfile): FormState {
  return {
    legalEntityName: p.legalEntityName,
    dba: p.dba ?? '',
    entityType: p.entityType,
    stateOfFormation: p.stateOfFormation,
    ein: p.ein ?? '',
    registeredAddress: addressToForm(p.registeredAddress),
    sameAsRegistered: !p.mailingAddress,
    mailingAddress: addressToForm(p.mailingAddress ?? p.registeredAddress),
    phone: p.phone,
    website: p.website ?? '',
    supportEmail: p.supportEmail,
    billingEmail: p.billingEmail,
    accountsEmail: p.accountsEmail,
    salesEmail: p.salesEmail,
    adminEmail: p.adminEmail,
    notificationEmails: [...p.notificationEmails],
    signatories: p.signatories ?? [],
    defaultSignatoryId: p.defaultSignatoryId ?? '',
    governingLawState: p.governingLawState,
    venueCounty: p.venueCounty,
  };
}

/** Trims a form address into wire shape. Blank line2 is omitted, not sent as "". */
function buildAddress(a: AddressForm): CompanyAddress {
  const out: CompanyAddress = {
    line1: a.line1.trim(),
    city: a.city.trim(),
    state: a.state.trim().toUpperCase(),
    postalCode: a.postalCode.trim(),
    country: (a.country.trim() || 'US').toUpperCase(),
  };
  const line2 = a.line2.trim();
  if (line2) out.line2 = line2;
  return out;
}

/** Blank optional strings become `undefined`, never `""` — the schema rejects a present-but-empty optional. */
const orUndefined = (s: string) => (s.trim() ? s.trim() : undefined);

/**
 * NEVER invent a legal fact the admin did not supply. `f.entityType` is `''`
 * until a human actually picks one — it must reach the schema as `''`, not
 * as some plausible-looking default, so `z.enum` rejects it and Save stays
 * disabled. (The one intentional exception is `buildAddress`'s `country`
 * fallback to `'US'` below: that reproduces the schema's OWN declared
 * `.default('US')`, so it inherits a stated default rather than fabricating
 * one. Every other field in this function follows the same rule: no `||
 * '<literal>'` unless the schema declares that exact default.) The cast
 * below is a deliberate, narrow lie to satisfy this function's return type
 * when the field is still blank — `PROFILE_SCHEMA.safeParse` is what
 * actually enforces the value, and `buildPayload`'s output is never sent to
 * the API except after that validation has already succeeded.
 */
function buildPayload(f: FormState): Omit<CompanyProfile, 'updatedAt' | 'updatedBy'> {
  return {
    legalEntityName: f.legalEntityName.trim(),
    dba: orUndefined(f.dba),
    entityType: f.entityType as CompanyEntityType,
    stateOfFormation: f.stateOfFormation.trim().toUpperCase(),
    ein: orUndefined(f.ein),
    registeredAddress: buildAddress(f.registeredAddress),
    mailingAddress: f.sameAsRegistered ? undefined : buildAddress(f.mailingAddress),
    phone: f.phone.trim(),
    website: orUndefined(f.website),
    supportEmail: f.supportEmail.trim(),
    billingEmail: f.billingEmail.trim(),
    accountsEmail: f.accountsEmail.trim(),
    salesEmail: f.salesEmail.trim(),
    adminEmail: f.adminEmail.trim(),
    notificationEmails: f.notificationEmails,
    governingLawState: f.governingLawState.trim().toUpperCase(),
    venueCounty: f.venueCounty.trim(),
    signatories: f.signatories,
    // Empty means "no default", not an empty id: the schema refuses a default
    // that names nobody, and '' would be exactly that.
    defaultSignatoryId: f.defaultSignatoryId || undefined,
  };
}

/**
 * WHO SIGNS FOR US — and, when their mark is stored, without waiting for them.
 *
 * A signatory with a stored signature lets an agreement reach `executed` the
 * moment the other party signs. One WITHOUT is still useful: they can be named
 * on an agreement and countersign by hand. Refusing to save them until they had
 * sat at a canvas would mean you could not assign a sales rep who is out today.
 *
 * The mark is captured HERE rather than at signing time, which is the whole
 * point: it is a standing authorisation, and the server stamps when it was
 * given so the executed certificate can show the authorisation predates the
 * signature it produced.
 */
function SignatoriesCard({
  signatories, defaultSignatoryId, onChange,
}: {
  signatories: CompanySignatory[];
  defaultSignatoryId: string;
  onChange: (next: CompanySignatory[], defaultId: string) => void;
}) {
  const [capturing, setCapturing] = useState<string | null>(null);

  const patch = (id: string, p: Partial<CompanySignatory>) => onChange(
    signatories.map((s) => (s.signatoryId === id ? { ...s, ...p } : s)),
    defaultSignatoryId,
  );

  const add = () => {
    // A readable id beats a uuid in an audit line and on an envelope row.
    const base = `sig_${Date.now().toString(36)}`;
    const next: CompanySignatory = {
      signatoryId: base, name: '', title: '', updatedAt: new Date().toISOString(),
    };
    onChange([...signatories, next], defaultSignatoryId || base);
  };

  const remove = (id: string) => onChange(
    signatories.filter((s) => s.signatoryId !== id),
    defaultSignatoryId === id ? '' : defaultSignatoryId,
  );

  return (
    <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm md:p-5">
      <h2 className="text-[13.5px] font-semibold text-stone-900">Who signs for us</h2>
      <p className="mt-0.5 text-[11.5px] text-stone-500">
        Normally the CEO. Store their signature and agreements countersign themselves the moment
        the other party signs; without one, they still get named and somebody countersigns by hand.
        Add sales reps here to assign them to their own deals.
      </p>

      <div className="mt-4 space-y-3">
        {signatories.length === 0 && (
          <p className="rounded-lg border border-dashed border-stone-300 px-3 py-4 text-center text-[12px] text-stone-500">
            Nobody is authorised to sign yet — every agreement will wait for a manual countersign.
          </p>
        )}

        {signatories.map((s) => {
          const isDefault = defaultSignatoryId === s.signatoryId;
          return (
            <div key={s.signatoryId} className="rounded-lg border border-stone-200 p-3">
              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <FieldLabel requirement="required">Name</FieldLabel>
                  <input
                    className={INPUT_CLASS}
                    value={s.name}
                    onChange={(e) => patch(s.signatoryId, { name: e.target.value })}
                    placeholder="Dana Reyes"
                  />
                </div>
                <div>
                  <FieldLabel requirement="required">Title</FieldLabel>
                  <input
                    className={INPUT_CLASS}
                    value={s.title}
                    onChange={(e) => patch(s.signatoryId, { title: e.target.value })}
                    placeholder="Chief Executive Officer"
                  />
                </div>
                <div>
                  <FieldLabel>Email</FieldLabel>
                  <input
                    className={INPUT_CLASS}
                    value={s.email ?? ''}
                    onChange={(e) => patch(s.signatoryId, { email: e.target.value || undefined })}
                    placeholder="dana@rubytill.com"
                  />
                </div>
                <div className="flex items-end gap-2">
                  <button
                    type="button"
                    onClick={() => onChange(signatories, s.signatoryId)}
                    className={`min-h-[38px] flex-1 rounded-lg border px-3 text-[12px] font-medium ${
                      isDefault
                        ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                        : 'border-stone-300 text-stone-600 hover:border-stone-400'
                    }`}
                  >
                    {isDefault ? 'Default signatory' : 'Make default'}
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(s.signatoryId)}
                    className="min-h-[38px] rounded-lg border border-stone-300 px-3 text-[12px] text-stone-500 hover:border-red-300 hover:text-red-600"
                  >
                    Remove
                  </button>
                </div>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-dashed border-stone-200 pt-3">
                {s.signature ? (
                  <>
                    <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
                      Signature stored ({s.signature.kind})
                    </span>
                    {s.signature.kind === 'drawn' && s.signature.imagePngBase64 && (
                      <img
                        src={`data:image/png;base64,${s.signature.imagePngBase64}`}
                        alt={`${s.name} signature`}
                        className="h-9 rounded border border-stone-200 bg-white"
                      />
                    )}
                    {s.signature.kind === 'typed' && (
                      <span className="text-[15px] italic text-stone-700">{s.signature.typedName}</span>
                    )}
                    <button
                      type="button"
                      onClick={() => patch(s.signatoryId, { signature: undefined })}
                      className="text-[11.5px] text-stone-500 underline hover:text-red-600"
                    >
                      Remove signature
                    </button>
                  </>
                ) : (
                  <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-700">
                    No signature — agreements wait for a manual countersign
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => setCapturing(capturing === s.signatoryId ? null : s.signatoryId)}
                  className="ml-auto min-h-[34px] rounded-lg border border-stone-300 px-3 text-[12px] font-semibold text-stone-600 hover:border-rose-300 hover:text-rose-600"
                >
                  {s.signature ? 'Replace signature' : 'Capture signature'}
                </button>
              </div>

              {capturing === s.signatoryId && (
                <SignatureCapture
                  name={s.name}
                  onCancel={() => setCapturing(null)}
                  onCapture={(sig) => {
                    // `capturedAt` is stamped by the SERVER on save — it is
                    // evidence, and a client clock could backdate the
                    // authorisation an automatic countersignature rests on.
                    patch(s.signatoryId, { signature: { ...sig, capturedAt: new Date().toISOString() } });
                    setCapturing(null);
                  }}
                />
              )}
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={add}
        className="mt-3 min-h-[38px] rounded-lg border border-dashed border-stone-300 px-3 text-[12px] font-semibold text-stone-500 hover:border-rose-300 hover:text-rose-600"
      >
        + Add signatory
      </button>
    </div>
  );
}

/**
 * Capture a mark: typed in a script face, or drawn.
 *
 * Deliberately NOT the `SignaturePad` used at signing time. That component
 * gates on consent and scroll-to-end because it captures a signature ON a
 * document somebody has just read. This captures a STANDING authorisation with
 * no document in front of it, and reusing a pad that demands "I have read the
 * above" would be asking for agreement to nothing.
 */
function SignatureCapture({
  name, onCapture, onCancel,
}: {
  name: string;
  onCapture: (s: { kind: 'typed' | 'drawn'; typedName?: string; imagePngBase64?: string }) => void;
  onCancel: () => void;
}) {
  const [tab, setTab] = useState<'typed' | 'drawn'>('typed');
  const [typedName, setTypedName] = useState(name);
  const [hasDrawn, setHasDrawn] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const last = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => { setTypedName(name); }, [name]);

  const point = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const src: any = 'touches' in e ? (e as React.TouchEvent).touches[0] : e;
    return {
      x: ((src.clientX - rect.left) / rect.width) * canvas.width,
      y: ((src.clientY - rect.top) / rect.height) * canvas.height,
    };
  };

  const start = (e: React.MouseEvent | React.TouchEvent) => {
    drawing.current = true;
    last.current = point(e);
  };

  const move = (e: React.MouseEvent | React.TouchEvent) => {
    if (!drawing.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx || !last.current) return;
    const p = point(e);
    ctx.strokeStyle = '#1c1917';
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    setHasDrawn(true);
  };

  const stop = () => { drawing.current = false; last.current = null; };

  const clear = () => {
    const canvas = canvasRef.current;
    canvas?.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  const commit = () => {
    if (tab === 'typed') {
      if (!typedName.trim()) return;
      onCapture({ kind: 'typed', typedName: typedName.trim() });
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas || !hasDrawn) return;
    // The data-URL prefix is stripped: the schema stores raw base64, and the
    // service decodes it straight into PNG bytes.
    onCapture({ kind: 'drawn', imagePngBase64: canvas.toDataURL('image/png').split(',')[1] });
  };

  return (
    <div className="mt-3 rounded-lg border border-stone-300 bg-stone-50 p-3">
      <div className="mb-2 flex gap-1.5">
        {(['typed', 'drawn'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`min-h-[32px] rounded-md border px-3 text-[12px] font-medium ${
              tab === t ? 'border-rose-300 bg-rose-50 text-rose-700' : 'border-stone-300 text-stone-600'
            }`}
          >
            {t === 'typed' ? 'Type it' : 'Draw it'}
          </button>
        ))}
      </div>

      {tab === 'typed' ? (
        <div>
          <input
            className={INPUT_CLASS}
            value={typedName}
            onChange={(e) => setTypedName(e.target.value)}
            placeholder="Full legal name"
          />
          <div className="mt-2 rounded-md border border-stone-200 bg-white px-3 py-3 text-[22px] italic text-stone-800">
            {typedName || 'Your name'}
          </div>
          <p className="mt-1.5 text-[11px] text-stone-500">
            A typed name is legally equivalent to a drawn mark — the mark evidences intent, and the
            record evidences who and when.
          </p>
        </div>
      ) : (
        <div>
          <canvas
            ref={canvasRef}
            width={600}
            height={160}
            className="w-full cursor-crosshair rounded-md border border-stone-300 bg-white"
            onMouseDown={start}
            onMouseMove={move}
            onMouseUp={stop}
            onMouseLeave={stop}
            onTouchStart={start}
            onTouchMove={move}
            onTouchEnd={stop}
          />
          <button
            type="button"
            onClick={clear}
            className="mt-1.5 text-[11.5px] text-stone-500 underline hover:text-stone-800"
          >
            Clear
          </button>
        </div>
      )}

      <div className="mt-3 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="min-h-[34px] rounded-lg border border-stone-300 px-3 text-[12px] text-stone-600"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={commit}
          disabled={tab === 'typed' ? !typedName.trim() : !hasDrawn}
          className="min-h-[34px] rounded-lg px-3 text-[12px] font-semibold text-white disabled:opacity-50"
          style={{ background: '#DC143C' }}
        >
          Use this signature
        </button>
      </div>
    </div>
  );
}

function SectionCard({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm md:p-5">
      <h2 className="text-[13.5px] font-semibold text-stone-900">{title}</h2>
      {hint && <p className="mt-0.5 text-[11.5px] text-stone-500">{hint}</p>}
      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">{children}</div>
    </div>
  );
}

function Field({
  label, requirement, full, error, children,
}: {
  label: string; requirement?: 'required'; full?: boolean; error?: string; children: React.ReactNode;
}) {
  return (
    <div className={full ? 'md:col-span-2' : undefined}>
      <FieldLabel requirement={requirement}>{label}</FieldLabel>
      {children}
      {error && <p className="mt-1.5 text-[11.5px] text-red-600">{error}</p>}
    </div>
  );
}

function AddressFields({
  value, onChange, prefix, errorFor,
}: {
  value: AddressForm;
  onChange: (a: AddressForm) => void;
  prefix: string;
  /** Looks up the inline message for one leaf field — see `fieldError` in the page component. */
  errorFor: (leaf: keyof AddressForm) => string | undefined;
}) {
  return (
    <>
      <Field label="Address line 1" requirement="required" full error={errorFor('line1')}>
        <input
          className={INPUT_CLASS}
          value={value.line1}
          onChange={(e) => onChange({ ...value, line1: e.target.value })}
          placeholder="100 Main St"
          aria-label={`${prefix} address line 1`}
        />
      </Field>
      <Field label="Address line 2" full error={errorFor('line2')}>
        <input
          className={INPUT_CLASS}
          value={value.line2}
          onChange={(e) => onChange({ ...value, line2: e.target.value })}
          placeholder="Suite 2"
          aria-label={`${prefix} address line 2`}
        />
      </Field>
      <Field label="City" requirement="required" error={errorFor('city')}>
        <input
          className={INPUT_CLASS}
          value={value.city}
          onChange={(e) => onChange({ ...value, city: e.target.value })}
          placeholder="Norfolk"
          aria-label={`${prefix} city`}
        />
      </Field>
      <Field label="State" requirement="required" error={errorFor('state')}>
        <SearchableDropdown
          options={STATE_OPTIONS}
          value={value.state || null}
          onChange={(v) => onChange({ ...value, state: v || '' })}
          placeholder="Choose a state…"
        />
      </Field>
      <Field label="Postal code" requirement="required" error={errorFor('postalCode')}>
        <input
          className={INPUT_CLASS}
          value={value.postalCode}
          onChange={(e) => onChange({ ...value, postalCode: e.target.value })}
          placeholder="23510"
          aria-label={`${prefix} postal code`}
        />
      </Field>
      <Field label="Country" error={errorFor('country')}>
        <input
          className={INPUT_CLASS}
          value={value.country}
          onChange={(e) => onChange({ ...value, country: e.target.value })}
          placeholder="US"
          aria-label={`${prefix} country`}
        />
      </Field>
    </>
  );
}

export default function CompanyProfilePage() {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [configured, setConfigured] = useState(false);
  const [meta, setMeta] = useState<{ updatedAt: string; updatedBy: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newNotifyEmail, setNewNotifyEmail] = useState('');
  const [notifyError, setNotifyError] = useState<string | null>(null);
  // Has the user touched this form at all since it was last (re)loaded?
  // Gates inline field errors: a freshly opened form — even one that is
  // entirely invalid (blank) — must not read as a wall of red. The FIRST
  // edit flips this on for the whole form, not just the field edited,
  // because Save is disabled (not just visually greyed) the instant the
  // form is invalid, so there is no separate "attempted to submit" click to
  // hang this on — Save is unclickable while `canSave` is false, by design
  // (see Finding 2). Whole-form gating is what actually surfaces an
  // untouched-but-required field, like Entity type, once the admin has
  // engaged with the form at all instead of leaving it looking silently
  // fine.
  const [interacted, setInteracted] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { profile, configured: isConfigured } = await documentsExtApi.getCompanyProfile();
      if (profile) {
        setForm(profileToForm(profile));
        setMeta({ updatedAt: profile.updatedAt, updatedBy: profile.updatedBy });
      } else {
        setForm(EMPTY_FORM);
        setMeta(null);
      }
      setConfigured(isConfigured);
      setInteracted(false);
    } catch (e: any) {
      toastService.error(e?.message || 'company_profile_load_failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  /** Every field edit goes through this — the one place that marks the form interacted. */
  const patchForm = (patch: Partial<FormState>) => {
    setInteracted(true);
    setForm((f) => ({ ...f, ...patch }));
  };

  // `PROFILE_SCHEMA` is null only when the schema failed to load (see the
  // guard above) — in that case there is nothing to validate against, so
  // treat every payload as unvalidated rather than either valid or invalid.
  const validation = useMemo(
    () => (PROFILE_SCHEMA ? PROFILE_SCHEMA.safeParse(buildPayload(form)) : null),
    [form],
  );
  // Never permissive when the schema is missing: a page that cannot
  // validate must not be able to submit a legal-identity payload.
  const canSave = !loading && !saving && !!validation?.success;

  /**
   * One message per field path, from the SAME `validation` that gates
   * `canSave` — never a second, drifting source of truth. Zod issue paths
   * are arrays (`['registeredAddress', 'postalCode']` for a nested
   * address), so they are joined with '.' to match the dotted paths used
   * below; a path with more than one issue keeps the first (the schema
   * checks fields roughly in declaration order, so that is usually the
   * most useful one to show).
   */
  const issuesByPath = useMemo(() => {
    const map: Record<string, string> = {};
    if (validation && !validation.success) {
      for (const issue of validation.error.issues) {
        const key = issue.path.join('.');
        if (!(key in map)) map[key] = issue.message;
      }
    }
    return map;
  }, [validation]);

  /** Inline message for one field — hidden until the form has been touched. See `interacted`. */
  const fieldError = (path: string): string | undefined => (interacted ? issuesByPath[path] : undefined);

  const addNotifyEmail = () => {
    const email = newNotifyEmail.trim();
    if (!email) return;
    // Reuse the schema's own email validator (same rule as supportEmail etc.)
    // rather than duplicating a regex — the array field's default() wrapper
    // does not expose .element, so a plain required-email field stands in.
    // Falls back to a plain email shape check if the schema itself failed
    // to load (see the PROFILE_SCHEMA guard above) — degrade the rule
    // rather than crash the click handler.
    const check = CompanyProfileFieldsSchema
      && typeof CompanyProfileFieldsSchema.shape?.supportEmail?.safeParse === 'function'
      ? CompanyProfileFieldsSchema.shape.supportEmail.safeParse(email)
      : { success: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) };
    if (!check.success) { setNotifyError('Not a valid email address.'); return; }
    if (form.notificationEmails.includes(email)) { setNotifyError('Already in the list.'); return; }
    setInteracted(true);
    setForm({ ...form, notificationEmails: [...form.notificationEmails, email] });
    setNewNotifyEmail('');
    setNotifyError(null);
  };

  const removeNotifyEmail = (email: string) => {
    setInteracted(true);
    setForm({ ...form, notificationEmails: form.notificationEmails.filter((e) => e !== email) });
  };

  const save = async () => {
    if (!validation?.success) return;
    setSaving(true);
    try {
      // validation.success already proved buildPayload(form) parses cleanly
      // against the server's own schema — send that value (typed explicitly
      // as Omit<CompanyProfile, 'updatedAt'|'updatedBy'>) rather than
      // validation.data, whose type TS cannot always resolve through a
      // dynamically-omitted 20-field zod object.
      const saved = await documentsExtApi.saveCompanyProfile(buildPayload(form));
      setForm(profileToForm(saved));
      setMeta({ updatedAt: saved.updatedAt, updatedBy: saved.updatedBy });
      setConfigured(true);
      setInteracted(false);
      toastService.success('Company profile saved');
    } catch (e: any) {
      toastService.error(e?.message || 'company_profile_save_failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <StaffPage
      section="Legal"
      tabs={<LegalNav />}
      icon={Building2}
      title="Company Profile"
      subtitle="Our own legal identity — the source every generated document reads its name, address and role mailboxes from."
      actions={(
        <>
          <Button onClick={() => void load()} disabled={loading || saving}>
            {loading ? <RubyGemMark size={16} drawing color="currentColor" /> : <RefreshCw className="h-4 w-4" />}
            Refresh
          </Button>
          <Button variant="primary" onClick={() => void save()} disabled={!canSave}>
            {saving ? <RubyGemMark size={16} drawing color="currentColor" /> : <Save className="h-4 w-4" />}
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </>
      )}
    >
      {loading ? (
        <RubyLoader variant="page" label="Loading company profile…" />
      ) : (
        <div className="space-y-4">
          {!PROFILE_SCHEMA && (
            <div className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 px-3.5 py-3 text-[12.5px] leading-relaxed text-red-800">
              <AlertTriangle size={15} className="mt-0.5 shrink-0" />
              <span>
                This form cannot validate right now — <b>Save is disabled</b>. The linked
                shared-types package loaded without its company profile schema, which
                normally means the dev server is running against a stale build.
                Restart the dev server (deleting <code>node_modules/.vite</code> alone
                will not fix it while the server keeps running) and reload this page.
              </span>
            </div>
          )}

          {!configured && (
            <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3.5 py-3 text-[12.5px] leading-relaxed text-blue-800">
              <Info size={15} className="mt-0.5 shrink-0" />
              <span>
                No company profile saved yet. Every generated document reads its company details
                from this one row.
              </span>
            </div>
          )}

          <SectionCard title="Legal Entity" hint="The name and formation facts printed on every agreement.">
            <Field label="Legal entity name" requirement="required" full error={fieldError('legalEntityName')}>
              <input
                className={INPUT_CLASS}
                value={form.legalEntityName}
                onChange={(e) => patchForm({ legalEntityName: e.target.value })}
                placeholder="Ruby Till, LLC"
              />
            </Field>
            <Field label="DBA (trading name)" error={fieldError('dba')}>
              <input
                className={INPUT_CLASS}
                value={form.dba}
                onChange={(e) => patchForm({ dba: e.target.value })}
                placeholder="Ruby Till"
              />
            </Field>
            <Field label="Entity type" requirement="required" error={fieldError('entityType')}>
              <SearchableDropdown
                options={ENTITY_TYPE_OPTIONS}
                value={form.entityType || null}
                onChange={(v) => patchForm({ entityType: (v as CompanyEntityType) || '' })}
                placeholder="Choose an entity type…"
              />
            </Field>
            <Field label="State of formation" requirement="required" error={fieldError('stateOfFormation')}>
              <SearchableDropdown
                options={STATE_OPTIONS}
                value={form.stateOfFormation || null}
                onChange={(v) => patchForm({ stateOfFormation: v || '' })}
                placeholder="Choose a state…"
              />
            </Field>
            <Field label="EIN" error={fieldError('ein')}>
              <input
                className={INPUT_CLASS}
                value={form.ein}
                onChange={(e) => patchForm({ ein: e.target.value })}
                placeholder="12-3456789"
              />
            </Field>
          </SectionCard>

          <SectionCard title="Addresses" hint="Registered is the legal address of record. Mailing defaults to the same place.">
            <div className="md:col-span-2">
              <p className="mb-2 text-[11.5px] font-semibold uppercase tracking-wider text-stone-500">
                Registered address
              </p>
            </div>
            <AddressFields
              prefix="Registered"
              value={form.registeredAddress}
              onChange={(a) => patchForm({ registeredAddress: a })}
              errorFor={(leaf) => fieldError(`registeredAddress.${leaf}`)}
            />

            <div className="md:col-span-2 mt-2 flex items-center gap-2 border-t border-stone-100 pt-4">
              <label className="flex min-h-[44px] cursor-pointer items-center gap-2 text-[12.5px] text-stone-700">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={form.sameAsRegistered}
                  onChange={(e) => patchForm({ sameAsRegistered: e.target.checked })}
                />
                Mailing address is the same as registered
              </label>
            </div>

            {!form.sameAsRegistered && (
              <>
                <div className="md:col-span-2">
                  <p className="mb-2 text-[11.5px] font-semibold uppercase tracking-wider text-stone-500">
                    Mailing address
                  </p>
                </div>
                <AddressFields
                  prefix="Mailing"
                  value={form.mailingAddress}
                  onChange={(a) => patchForm({ mailingAddress: a })}
                  errorFor={(leaf) => fieldError(`mailingAddress.${leaf}`)}
                />
              </>
            )}
          </SectionCard>

          <SectionCard title="Contact" hint="Shown on documents alongside the legal name.">
            <Field label="Phone" requirement="required" error={fieldError('phone')}>
              <input
                className={INPUT_CLASS}
                value={form.phone}
                onChange={(e) => patchForm({ phone: e.target.value })}
                placeholder="(555) 123-4567"
              />
            </Field>
            <Field label="Website" error={fieldError('website')}>
              <input
                className={INPUT_CLASS}
                value={form.website}
                onChange={(e) => patchForm({ website: e.target.value })}
                placeholder="https://rubytill.com"
              />
            </Field>
          </SectionCard>

          <SectionCard title="Role Mailboxes" hint="Every outbound address the platform sends from or reads a merge field for.">
            <Field label="Support email" requirement="required" error={fieldError('supportEmail')}>
              <input
                className={INPUT_CLASS}
                type="email"
                value={form.supportEmail}
                onChange={(e) => patchForm({ supportEmail: e.target.value })}
                placeholder="support@rubytill.com"
              />
            </Field>
            <Field label="Billing email" requirement="required" error={fieldError('billingEmail')}>
              <input
                className={INPUT_CLASS}
                type="email"
                value={form.billingEmail}
                onChange={(e) => patchForm({ billingEmail: e.target.value })}
                placeholder="billing@rubytill.com"
              />
            </Field>
            <Field label="Accounts email" requirement="required" error={fieldError('accountsEmail')}>
              <input
                className={INPUT_CLASS}
                type="email"
                value={form.accountsEmail}
                onChange={(e) => patchForm({ accountsEmail: e.target.value })}
                placeholder="accounts@rubytill.com"
              />
            </Field>
            <Field label="Sales email" requirement="required" error={fieldError('salesEmail')}>
              <input
                className={INPUT_CLASS}
                type="email"
                value={form.salesEmail}
                onChange={(e) => patchForm({ salesEmail: e.target.value })}
                placeholder="sales@rubytill.com"
              />
            </Field>
            <Field label="Admin email" requirement="required" full error={fieldError('adminEmail')}>
              <input
                className={INPUT_CLASS}
                type="email"
                value={form.adminEmail}
                onChange={(e) => patchForm({ adminEmail: e.target.value })}
                placeholder="admin@rubytill.com"
              />
            </Field>

            <Field label="Notification emails" full error={fieldError('notificationEmails')}>
              <div className="flex flex-col gap-2 sm:flex-row">
                <input
                  className={INPUT_CLASS}
                  type="email"
                  value={newNotifyEmail}
                  onChange={(e) => { setNewNotifyEmail(e.target.value); setNotifyError(null); }}
                  placeholder="ops@rubytill.com"
                />
                <button
                  type="button"
                  onClick={addNotifyEmail}
                  disabled={!newNotifyEmail.trim()}
                  className="inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-lg border border-stone-300 bg-white px-4 text-[13px] text-stone-700 hover:bg-stone-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Plus className="h-4 w-4" /> Add
                </button>
              </div>
              {notifyError && <p className="mt-1.5 text-[11.5px] text-red-600">{notifyError}</p>}
              <p className="mt-1.5 text-[11.5px] text-stone-500">Who gets internal notifications. Optional — may be empty.</p>
              {form.notificationEmails.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {form.notificationEmails.map((email) => (
                    <span
                      key={email}
                      className="inline-flex min-h-[32px] items-center gap-1.5 rounded-full border border-stone-300 bg-stone-50 px-3 py-1 text-[12px] text-stone-700"
                    >
                      <Mail size={12} className="text-stone-400" />
                      {email}
                      <button
                        type="button"
                        aria-label={`Remove ${email}`}
                        onClick={() => removeNotifyEmail(email)}
                        className="ml-0.5 flex h-5 w-5 items-center justify-center rounded-full text-stone-400 hover:bg-stone-200 hover:text-stone-700"
                      >
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </Field>
          </SectionCard>

          <SectionCard title="Legal Terms" hint="Drives the governing-law and venue clauses in every agreement.">
            <Field label="Governing law state" requirement="required" error={fieldError('governingLawState')}>
              <SearchableDropdown
                options={STATE_OPTIONS}
                value={form.governingLawState || null}
                onChange={(v) => patchForm({ governingLawState: v || '' })}
                placeholder="Choose a state…"
              />
            </Field>
            <Field label="Venue county" requirement="required" error={fieldError('venueCounty')}>
              <input
                className={INPUT_CLASS}
                value={form.venueCounty}
                onChange={(e) => patchForm({ venueCounty: e.target.value })}
                placeholder="Norfolk"
              />
            </Field>
          </SectionCard>

          <SignatoriesCard
            signatories={form.signatories}
            defaultSignatoryId={form.defaultSignatoryId}
            onChange={(signatories, defaultSignatoryId) => {
              setInteracted(true);
              setForm((f) => ({ ...f, signatories, defaultSignatoryId }));
            }}
          />

          {configured && meta && (
            <p className="px-1 text-[11.5px] text-stone-500">
              Last updated {new Date(meta.updatedAt).toLocaleString()} by {meta.updatedBy}
            </p>
          )}
        </div>
      )}
    </StaffPage>
  );
}
